const MONO_FONT = 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace';

/**
 * Table rendering shared by the editor and every read-only surface that shows
 * admin-authored HTML, so an authored table looks the same to the member as it
 * did to the person who built it.
 *
 * `table-layout: fixed` is what makes TipTap's column resizing meaningful — the
 * `<colgroup>` widths it writes are ignored under `auto` layout. The wrapper
 * scrolls on its own so a wide table never forces the page sideways.
 */
export const richHtmlTableSx = {
  '& .tableWrapper': { overflowX: 'auto', margin: '10px 0' },
  '& table': {
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
    width: '100%',
    margin: '10px 0',
    overflow: 'hidden',
  },
  '& th, & td': {
    border: '1px solid',
    borderColor: 'divider',
    padding: '6px 10px',
    verticalAlign: 'top',
    textAlign: 'left',
    minWidth: '3em',
    position: 'relative',
    '& > *:last-child': { marginBottom: 0 },
  },
  '& th': { backgroundColor: 'action.hover', fontWeight: 700 },
  // ProseMirror's cell selection + column resize affordances.
  '& .selectedCell::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    backgroundColor: 'primary.main',
    opacity: 0.12,
    pointerEvents: 'none',
  },
  '& .column-resize-handle': {
    position: 'absolute',
    right: -2,
    top: 0,
    bottom: 0,
    width: '4px',
    backgroundColor: 'primary.main',
    cursor: 'col-resize',
    zIndex: 1,
  },
} as const;

export const richHtmlSx = {
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    fontWeight: 700,
    lineHeight: 1.25,
    margin: '0.6em 0 0.3em',
    color: 'inherit',
  },
  '& h1': { fontSize: '1.6em' },
  '& h2': { fontSize: '1.35em' },
  '& h3': { fontSize: '1.15em' },
  '& h4': { fontSize: '1em' },
  '& h5, & h6': { fontSize: '0.9em', textTransform: 'uppercase', letterSpacing: '0.04em' },
  '& p': { margin: '0 0 0.6em' },
  '& p:last-child': { marginBottom: 0 },
  '& a': {
    color: 'primary.main',
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
    '&:hover': { textDecoration: 'none' },
  },
  '& ol': { listStyle: 'revert', pl: { xs: 3, md: 5 }, margin: 0 },
  '& ul': { listStyle: 'revert', pl: { xs: 3, md: 5 } },
  '& pre': {
    backgroundColor: 'grey.300',
    color: 'common.black',
    fontFamily: MONO_FONT,
    fontSize: 13,
    lineHeight: 1.5,
    padding: '12px 14px',
    borderRadius: 1,
    overflowX: 'auto',
    margin: '8px 0',
    whiteSpace: 'pre',
  },
  '& pre code': {
    background: 'transparent',
    color: 'inherit',
    padding: 0,
    fontSize: 'inherit',
    fontFamily: 'inherit',
    borderRadius: 0,
  },
  '& :not(pre) > code': {
    fontFamily: MONO_FONT,
    fontSize: '0.9em',
    backgroundColor: 'action.hover',
    padding: '1px 4px',
    borderRadius: 0.5,
  },
  '& blockquote': {
    borderLeft: '3px solid',
    borderColor: 'divider',
    margin: '8px 0',
    padding: '4px 12px',
    color: 'text.secondary',
  },
  '& img': { maxWidth: '100%', height: 'auto' },
  // Video embeds: 16:9 and full width of the measure, rather than the iframe's
  // 300x150 intrinsic default.
  '& iframe': {
    display: 'block',
    width: '100%',
    aspectRatio: '16 / 9',
    height: 'auto',
    border: 0,
    borderRadius: 1,
    margin: '8px 0',
  },
  ...richHtmlTableSx,
};
